const sectionSelector = '.fill-shop-the-look';
const configuratorSelector = '[data-fill-look-configurator]';
const dataSelector = '[data-fill-look-data]';
const formSelector = '[data-fill-look-form]';
const rowsSelector = '[data-fill-look-rows]';
const errorSelector = '[data-fill-look-error]';
const statusSelector = '[data-fill-look-status]';
const submitSelector = '[data-fill-look-submit]';
const submitTextSelector = '[data-fill-look-submit-text]';
const countSelector = '[data-fill-look-count]';
const totalSelector = '[data-fill-look-total]';
const optionValueSelector = '[data-fill-look-option-value]';
const includeSelector = '[data-fill-look-include]';
const optionsToggleSelector = '[data-fill-look-options-toggle]';
const stateKey = '__fillLookConfiguratorState';
const readyAttribute = 'data-fill-shop-the-look-ready';
const submittingAttribute = 'data-fill-look-submitting';
const defaultErrorMessage = "We couldn't add this look. Please try again.";
const selectOneMessage = 'Select at least one piece to continue.';

function parseJson(script) {
  try {
    return JSON.parse(script?.textContent || '{}');
  } catch (error) {
    console.warn('[fill-shop-the-look] Invalid configurator JSON', error);
    return {};
  }
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (character) => {
    const entities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return entities[character];
  });
}

function isDefaultOnlyProduct(product) {
  return product.variants?.length === 1;
}

function hasAvailableVariants(product) {
  const sharedRule = window.FillProductBundleState?.hasAvailableVariants;
  if (typeof sharedRule === 'function') return sharedRule(product);
  return (product.variants || []).some((variant) => variant?.id && variant.available === true);
}

function isVariantPurchasable(variant) {
  const sharedRule = window.FillProductBundleState?.isVariantPurchasable;
  if (typeof sharedRule === 'function') return sharedRule(variant);
  const variantId = Number(variant?.id);
  return Boolean(Number.isFinite(variantId) && variantId > 0 && variant.available === true);
}

function getInitialSelections(product) {
  if (!product.options?.length || isDefaultOnlyProduct(product)) {
    return product.variants?.[0]?.options ? [...product.variants[0].options] : [];
  }

  return product.options.map(() => null);
}

function findSelectedVariant(product, selections) {
  if (isDefaultOnlyProduct(product)) {
    const variant = product.variants?.[0];
    return variant?.available ? variant : null;
  }

  if (!product.options?.every((_, index) => selections[index])) return null;

  return (
    product.variants?.find((variant) => {
      return variant.available && variant.options?.every((option, index) => option === selections[index]);
    }) || null
  );
}

function getVariantForState(item) {
  return findSelectedVariant(item.product, item.selections);
}

function formatCents(cents, precision = 2, thousands = ',', decimal = '.') {
  const amount = (Number(cents) / 100).toFixed(precision);
  const [whole, fraction] = amount.split('.');
  const wholeWithSeparators = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

  return precision > 0 ? `${wholeWithSeparators}${decimal}${fraction}` : wholeWithSeparators;
}

function formatPrice(config, price) {
  const format = config.moneyFormat || '${{amount}}';
  const cents = Number(price) || 0;
  const placeholderMatch = format.match(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/);
  const placeholder = placeholderMatch?.[0] || '{{amount}}';
  const placeholderName = placeholderMatch?.[1] || 'amount';
  let value;

  switch (placeholderName) {
    case 'amount_no_decimals':
      value = formatCents(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatCents(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatCents(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatCents(cents, 0, ' ');
      break;
    case 'amount_with_space_separator':
      value = formatCents(cents, 2, ' ', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatCents(cents, 2, "'", '.');
      break;
    default:
      value = formatCents(cents);
  }

  return format.replace(placeholder, value);
}

function getDisplayPrice(state, item) {
  const selectedVariant = getVariantForState(item);
  if (selectedVariant) return formatPrice(state.config, selectedVariant.price);

  const availableVariants = (item.product.variants || []).filter((variant) => variant.available);
  const priceVariants = availableVariants.length ? availableVariants : (item.product.variants || []);
  if (!priceVariants.length) return 'Unavailable';

  const prices = priceVariants.map((variant) => Number(variant.price) || 0);
  const lowestPrice = Math.min(...prices);
  return `${new Set(prices).size > 1 ? 'From ' : ''}${formatPrice(state.config, lowestPrice)}`;
}

function getVariantForTotal(item) {
  const selectedVariant = getVariantForState(item);
  if (selectedVariant) return selectedVariant;

  const compatibleVariants = (item.product.variants || []).filter((variant) => {
    if (!variant.available) return false;

    return variant.options?.every((optionValue, optionIndex) => {
      const selectedValue = item.selections[optionIndex];
      return !selectedValue || selectedValue === optionValue;
    });
  });

  return compatibleVariants.reduce((lowestVariant, variant) => {
    if (!lowestVariant) return variant;
    return Number(variant.price) < Number(lowestVariant.price) ? variant : lowestVariant;
  }, null);
}

function getCartItemsSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
    .filter(Boolean);
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getErrorMessage(error) {
  const data = error?.data || error;

  if (typeof data?.description === 'string' && data.description.trim()) return data.description;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (typeof data?.error === 'string' && data.error.trim()) return data.error;

  if (data?.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors).flat().find(Boolean);
    if (typeof firstError === 'string' && firstError.trim()) return firstError;
  }

  if (typeof error?.message === 'string' && error.message.trim()) return error.message;

  return defaultErrorMessage;
}

async function parseCartResponse(response) {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    if (!response.ok) throw new Error(defaultErrorMessage);
    return {};
  }
}

function getCartUrl(config) {
  return config.routes?.cartUrl || window.Theme?.routes?.cart_url || '/cart';
}

async function fetchCart(config) {
  const cartUrl = `${getCartUrl(config)}.json`;
  const response = await fetch(cartUrl, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(defaultErrorMessage);

  return response.json();
}

function setError(state, message) {
  state.errorElement.textContent = message || defaultErrorMessage;
  state.errorElement.hidden = false;
  state.statusElement.textContent = message || defaultErrorMessage;
}

function clearError(state) {
  state.errorElement.textContent = '';
  state.errorElement.hidden = true;
}

function setSubmitting(state, isSubmitting) {
  state.root.toggleAttribute(submittingAttribute, isSubmitting);
  state.submitButton.disabled = isSubmitting || state.selectedCount === 0;
  state.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');
  state.submitText.textContent = isSubmitting ? 'Adding...' : state.availableCount === 0 ? 'Sold Out' : 'Add the Look';
}

function isValueAvailable(product, optionIndex, value, selections) {
  return product.variants?.some((variant) => {
    if (!variant.available || variant.options?.[optionIndex] !== value) return false;

    return variant.options.every((optionValue, index) => {
      return index === optionIndex || !selections[index] || selections[index] === optionValue;
    });
  });
}

function renderOptionControl(state, item, option, optionIndex) {
  const product = item.product;
  const selectedValue = item.selections[optionIndex];
  const values = option.values || [];
  const rowId = `${state.root.id}-product-${product.id}-option-${optionIndex}`;
  const errorId = `${state.root.id}-product-${product.id}-error`;
  const disabled = item.included ? '' : ' disabled';

  return `
    <label class="fill-shop-the-look__select-label" for="${rowId}">
      <span>${escapeHtml(option.name)}</span>
      <select
        id="${rowId}"
        class="fill-shop-the-look__select"
        data-product-id="${escapeHtml(product.id)}"
        data-option-index="${optionIndex}"
        data-fill-look-option-value
        aria-label="${escapeHtml(`${option.name} for ${product.title}`)}"
        aria-describedby="${errorId}"
        ${item.hasError ? 'aria-invalid="true"' : ''}
        ${disabled}
      >
        <option value="">Select ${escapeHtml(String(option.name).toLowerCase())}</option>
        ${values
          .map((value) => {
            const available = isValueAvailable(product, optionIndex, value, item.selections);
            return `
              <option value="${escapeHtml(value)}"${selectedValue === value ? ' selected' : ''}${available ? '' : ' disabled'}>
                ${escapeHtml(value)}${available ? '' : ' - sold out'}
              </option>
            `;
          })
          .join('')}
      </select>
    </label>
  `;
}

function getSelectionSummary(item) {
  const product = item.product;
  if (item.automaticallyExcluded) return 'SOLD OUT';
  if (!item.included) return 'Not included';
  if (isDefaultOnlyProduct(product)) return 'Included';

  const summaryParts = (product.options || []).map((option, optionIndex) => {
    const selectedValue = item.selections[optionIndex];
    return selectedValue ? `${option.name}: ${selectedValue}` : `Select ${String(option.name).toLowerCase()}`;
  });

  if (summaryParts.every((part) => part.startsWith('Select ')) && summaryParts.length > 1) {
    return 'Select options';
  }

  return summaryParts.join(' · ');
}

function renderRow(state, item, itemIndex) {
  const product = item.product;
  const variant = getVariantForState(item);
  const displayImage = variant?.featuredImage || product.featuredImage || '';
  const hasOptions = product.options?.length && !isDefaultOnlyProduct(product);
  const componentAvailable = hasAvailableVariants(product);
  const rowId = `${state.root.id}-product-${product.id}`;
  const errorId = `${rowId}-error`;
  const rowClasses = [
    'fill-shop-the-look__item',
    `fill-shop-the-look__item--${itemIndex + 1}`,
    item.included || item.automaticallyExcluded ? '' : 'fill-shop-the-look__item--removed',
    item.expanded ? 'fill-shop-the-look__item--expanded' : '',
    componentAvailable ? '' : 'fill-shop-the-look__item--unavailable',
    item.hasError ? 'fill-shop-the-look__item--error' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return `
    <li id="${rowId}" class="${rowClasses}" data-fill-look-row data-product-id="${escapeHtml(product.id)}">
      <a class="fill-shop-the-look__thumb-link" href="${escapeHtml(product.url)}" aria-label="${escapeHtml(`View ${product.title}`)}">
        <div class="fill-shop-the-look__thumb" aria-hidden="true">
          ${displayImage ? `<img class="fill-shop-the-look__thumb-image" src="${escapeHtml(displayImage)}" alt="" loading="lazy">` : ''}
        </div>
      </a>

      <div class="fill-shop-the-look__item-content">
        <div class="fill-shop-the-look__item-header">
          <div class="fill-shop-the-look__item-copy">
            <a class="fill-shop-the-look__name fill-shop-the-look__name-link" href="${escapeHtml(product.url)}">
              ${escapeHtml(product.title)}
            </a>
            ${item.automaticallyExcluded ? '<span class="fill-shop-the-look__availability">SOLD OUT</span>' : ''}
          </div>
          <span class="fill-shop-the-look__price">${escapeHtml(getDisplayPrice(state, item))}</span>
        </div>

        ${
          hasOptions && componentAvailable
            ? `<div id="${rowId}-options" class="fill-shop-the-look__options"${item.expanded && item.included ? '' : ' hidden'}>
                ${product.options.map((option, optionIndex) => renderOptionControl(state, item, option, optionIndex)).join('')}
              </div>`
            : ''
        }

        <div class="fill-shop-the-look__compact-meta">
          <span class="fill-shop-the-look__selection-summary">${escapeHtml(getSelectionSummary(item))}</span>
          <span class="fill-shop-the-look__item-actions">
            ${
              hasOptions && componentAvailable && item.included
                ? `<button
                    class="fill-shop-the-look__option-action"
                    type="button"
                    data-product-id="${escapeHtml(product.id)}"
                    data-fill-look-options-toggle
                    aria-expanded="${item.expanded ? 'true' : 'false'}"
                    aria-controls="${rowId}-options"
                  >
                    ${item.expanded ? 'Done' : variant ? 'Change' : 'Choose'}
                  </button>`
                : ''
            }
            ${
              item.automaticallyExcluded
                ? ''
                : `<button
                    class="fill-shop-the-look__include-action"
                    type="button"
                    data-product-id="${escapeHtml(product.id)}"
                    data-fill-look-include
                    aria-pressed="${item.included ? 'true' : 'false'}"
                    aria-label="${escapeHtml(item.included ? `Remove ${product.title} from this look` : `Add ${product.title} back to this look`)}"
                  >
                    ${item.included ? 'Remove' : 'Add back'}
                  </button>`
            }
          </span>
        </div>

        <p id="${errorId}" class="fill-shop-the-look__item-error"${item.hasError ? '' : ' hidden'}>
          ${item.hasError ? escapeHtml(item.errorMessage) : ''}
        </p>
      </div>
    </li>
  `;
}

function updateSummary(state) {
  const includedItems = state.items.filter((item) => item.included);
  const availableItems = state.items.filter((item) => !item.automaticallyExcluded);
  const pricedVariants = includedItems.map(getVariantForTotal).filter(Boolean);
  const total = pricedVariants.reduce((sum, variant) => sum + (Number(variant.price) || 0), 0);
  const hasCompleteSelections = includedItems.every((item) => Boolean(getVariantForState(item)));
  const piecesLabel = `${includedItems.length} ${includedItems.length === 1 ? 'piece' : 'pieces'} selected`;

  state.selectedCount = includedItems.length;
  state.availableCount = availableItems.length;
  state.countElement.textContent = includedItems.length === 0 ? '0 pieces selected' : piecesLabel;
  state.totalElement.textContent = availableItems.length === 0
    ? 'Sold Out'
    : hasCompleteSelections
      ? formatPrice(state.config, total)
      : 'Select options to view total';
  state.totalElement.dataset.fillLookTotalState = availableItems.length === 0
    ? 'sold-out'
    : hasCompleteSelections
      ? 'complete'
      : 'options-required';
  state.submitButton.disabled = state.root.hasAttribute(submittingAttribute) || includedItems.length === 0;
  state.submitText.textContent = availableItems.length === 0 ? 'Sold Out' : 'Add the Look';

  if (includedItems.length === 0) {
    setError(state, selectOneMessage);
  } else if (state.errorElement.textContent === selectOneMessage) {
    clearError(state);
  }
}

function renderRows(state) {
  state.rowsElement.innerHTML = state.items.map((item, index) => renderRow(state, item, index)).join('');
  updateSummary(state);
}

function resetState(state) {
  state.items = (state.config.components || []).map((product) => ({
    product,
    automaticallyExcluded: !hasAvailableVariants(product),
    included: hasAvailableVariants(product),
    selections: getInitialSelections(product),
    hasError: false,
    errorMessage: '',
    expanded: false,
  }));
  state.selectedCount = state.items.length;
  state.availableCount = state.items.filter((item) => !item.automaticallyExcluded).length;
  clearError(state);
  setSubmitting(state, false);
  renderRows(state);
}

function getItemByProductId(state, productId) {
  return state.items.find((item) => String(item.product.id) === String(productId));
}

function reconcileSelections(product, selections, changedOptionIndex) {
  const changedValue = selections[changedOptionIndex];
  if (!changedValue) return;

  let candidates = (product.variants || []).filter((variant) => {
    return variant.available && variant.options?.[changedOptionIndex] === changedValue;
  });

  selections.forEach((selectedValue, optionIndex) => {
    if (optionIndex === changedOptionIndex || !selectedValue) return;

    const matchingCandidates = candidates.filter((variant) => variant.options?.[optionIndex] === selectedValue);
    if (matchingCandidates.length) {
      candidates = matchingCandidates;
    } else {
      selections[optionIndex] = null;
    }
  });
}

function handleOptionChange(state, control) {
  const item = getItemByProductId(state, control.dataset.productId);
  if (!item) return;

  const optionIndex = Number(control.dataset.optionIndex);
  item.selections[optionIndex] = control.value || null;
  reconcileSelections(item.product, item.selections, optionIndex);
  item.hasError = false;
  item.errorMessage = '';
  const variant = getVariantForState(item);
  item.expanded = !variant;
  clearError(state);
  renderRows(state);

  const productId = CSS.escape(String(item.product.id));
  const optionControls = Array.from(
    state.rowsElement.querySelectorAll(`[data-product-id="${productId}"][data-option-index]:not(:disabled)`)
  );
  const focusTarget = variant
    ? state.rowsElement.querySelector(`[data-product-id="${productId}"][data-fill-look-options-toggle]`)
    : optionControls.find((candidate) => !candidate.value) || optionControls[0];
  focusTarget?.focus?.({ preventScroll: true });
}

function toggleOptions(state, productId) {
  const item = getItemByProductId(state, productId);
  if (!item || !item.included || isDefaultOnlyProduct(item.product)) return;

  const willExpand = !item.expanded;
  state.items.forEach((candidate) => {
    candidate.expanded = false;
  });
  item.expanded = willExpand;
  renderRows(state);

  const escapedProductId = CSS.escape(String(item.product.id));
  const row = state.rowsElement.querySelector(`[data-product-id="${escapedProductId}"][data-fill-look-row]`);
  const focusTarget = item.expanded
    ? row?.querySelector('select:not(:disabled)')
    : row?.querySelector(optionsToggleSelector);
  focusTarget?.focus?.({ preventScroll: true });
}

function setIncluded(state, productId) {
  const item = getItemByProductId(state, productId);
  if (!item || item.automaticallyExcluded) return;

  item.included = !item.included;
  item.expanded = false;
  item.hasError = false;
  item.errorMessage = '';
  clearError(state);
  renderRows(state);

  const escapedProductId = CSS.escape(String(item.product.id));
  state.rowsElement
    .querySelector(`[data-product-id="${escapedProductId}"][data-fill-look-include]`)
    ?.focus?.({ preventScroll: true });
}

function validateState(state) {
  const includedItems = state.items.filter((item) => item.included);

  state.items.forEach((item) => {
    item.hasError = false;
    item.errorMessage = '';
  });

  if (includedItems.length === 0) {
    setError(state, selectOneMessage);
    return false;
  }

  const item = includedItems.find((includedItem) => !getVariantForState(includedItem));
  if (item) {
    const missingOption = item.product.options?.find((_, index) => !item.selections[index]);
    state.items.forEach((candidate) => {
      candidate.expanded = false;
    });
    item.hasError = true;
    item.expanded = true;
    item.errorMessage = missingOption
      ? `Please select a ${missingOption.name.toLowerCase()}.`
      : 'This selection is currently unavailable.';
    setError(state, `${item.product.title}: ${item.errorMessage}`);
    renderRows(state);
    const row = state.rowsElement.querySelector(`[data-product-id="${CSS.escape(String(item.product.id))}"]`);
    row?.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    row?.querySelector('select:not(:disabled), button:not(:disabled)')?.focus({ preventScroll: true });
    return false;
  }

  clearError(state);
  return true;
}

function getCartLines(state) {
  return state.items
    .filter((item) => item.included && !item.automaticallyExcluded)
    .map((item) => {
      const variant = getVariantForState(item);
      if (!isVariantPurchasable(variant)) return null;
      const variantId = Number(variant.id);

      return {
        id: variantId,
        quantity: 1,
        properties: {
          _look_id: `look-${state.config.look?.id || state.config.look?.handle}`,
          _look_name: state.config.look?.title || 'Shop the Look',
          _look_parent: state.config.look?.handle || String(state.config.look?.id || ''),
        },
      };
    })
    .filter(Boolean);
}

async function addLookToCart(state, items) {
  const sectionIds = getCartItemsSectionIds();
  const payload = {
    items,
    sections: sectionIds.join(','),
  };

  const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
    credentials: 'same-origin',
  });
  const data = await parseCartResponse(response);

  if (!response.ok || data?.status) {
    const error = new Error(getErrorMessage(data));
    error.data = data;
    throw error;
  }

  return data;
}

async function handleSubmit(state, event) {
  event.preventDefault();

  if (state.root.hasAttribute(submittingAttribute)) return;
  if (!validateState(state)) return;

  const items = getCartLines(state);
  if (!items.length) {
    setError(state, selectOneMessage);
    return;
  }

  const { CartErrorEvent, CartLinesUpdateEvent } = await import('@shopify/events');
  const deferredEvent = CartLinesUpdateEvent.createPromise();
  const eventDetail = {
    source: 'fill-look-configurator',
    sourceId: state.root.id,
    productId: state.config.look?.id,
    itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
  };

  state.root.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({
        merchandiseId: String(item.id),
        quantity: item.quantity,
      })),
      promise: deferredEvent.promise,
      detail: eventDetail,
    })
  );

  setSubmitting(state, true);
  clearError(state);

  try {
    const addResponse = await addLookToCart(state, items);
    const cart = await fetchCart(state.config);

    deferredEvent.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        ...eventDetail,
        items: cart.items,
        sections: addResponse.sections,
        didError: false,
      },
    });

    state.statusElement.textContent = 'Look added to your bag.';
    window.location.assign(getCartUrl(state.config));
  } catch (error) {
    deferredEvent.reject(error);
    state.root.dispatchEvent(
      new CartErrorEvent({
        error: getErrorMessage(error),
        code: 'INVALID',
        detail: error?.data,
      })
    );
    setError(state, getErrorMessage(error));
    setSubmitting(state, false);
  }
}

function bindConfigurator(root) {
  if (root[stateKey]) return root[stateKey];

  const config = parseJson(root.querySelector(dataSelector));
  const state = {
    root,
    config,
    rowsElement: root.querySelector(rowsSelector),
    form: root.querySelector(formSelector),
    errorElement: root.querySelector(errorSelector),
    statusElement: root.querySelector(statusSelector),
    submitButton: root.querySelector(submitSelector),
    submitText: root.querySelector(submitTextSelector),
    countElement: root.querySelector(countSelector),
    totalElement: root.querySelector(totalSelector),
    selectedCount: 0,
    availableCount: 0,
    items: [],
  };

  if (
    !state.rowsElement ||
    !state.form ||
    !state.errorElement ||
    !state.statusElement ||
    !state.submitButton ||
    !state.submitText ||
    !state.countElement ||
    !state.totalElement
  ) {
    return null;
  }

  state.form.addEventListener('submit', (event) => handleSubmit(state, event));

  state.rowsElement.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;

    if (target.matches(optionValueSelector)) {
      handleOptionChange(state, target);
    }
  });

  state.rowsElement.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    if (button.matches(optionsToggleSelector)) {
      toggleOptions(state, button.dataset.productId);
    } else if (button.matches(includeSelector)) {
      setIncluded(state, button.dataset.productId);
    }
  });

  root[stateKey] = state;
  resetState(state);
  return state;
}

function initSection(section) {
  if (section.getAttribute(readyAttribute) === 'true') return;
  section.setAttribute(readyAttribute, 'true');

  section.querySelectorAll(configuratorSelector).forEach(bindConfigurator);
}

function initAllSections(root = document) {
  root.querySelectorAll(sectionSelector).forEach(initSection);
}

document.addEventListener('shopify:section:load', (event) => {
  initAllSections(event.target);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllSections(), { once: true });
} else {
  initAllSections();
}
